import { Box, Card, Flex, Icon, ScreenReaderOnly, Separator, theme, Typography } from '@fe-toolkit/web-components/ssr'

import { EVisitType, OrderDetailActiveAppointments } from '@giffgaff/broadband-common'

import { getAppointmentDateFormatted } from '@/components/pages/ChangeInstallDates/utils/utils'
import { appointmentUpdate } from '@/components/pages/ChangeInstallDates/ChangeInstallDates.container'

import { TManagedVisitType } from '../../utils/propTypeHelpers'

type OrderOverviewProps = {
  isApptUpdated: { PRE_WORK: boolean; IN_HOME: boolean }
  preWorkIsAmendable: boolean
  inHomeIsAmendable: boolean
  preWorkIsComplete: boolean
  inHomeIsComplete: boolean
  groupedActiveAppointments: {
    PRE_WORK?: OrderDetailActiveAppointments
    IN_HOME?: OrderDetailActiveAppointments
  }
  groupedUpdatedAppointments: {
    PRE_WORK?: appointmentUpdate
    IN_HOME?: appointmentUpdate
  }
}

const OrderOverview = ({
  isApptUpdated,
  groupedActiveAppointments,
  groupedUpdatedAppointments,
  preWorkIsAmendable,
  inHomeIsAmendable,
  preWorkIsComplete,
  inHomeIsComplete,
}: OrderOverviewProps) => {
  const getUpdatedAppointmentDateFormatted = (visitType: TManagedVisitType) => {
    return getAppointmentDateFormatted(
      groupedUpdatedAppointments[visitType]?.date,
      groupedUpdatedAppointments[visitType]?.slot.formattedArrivalTime,
    )
  }

  const getOriginalAppointmentDateFormatted = (visitType: TManagedVisitType) => {
    return getAppointmentDateFormatted(
      groupedActiveAppointments[visitType]?.appointmentDate,
      groupedActiveAppointments[visitType]?.formattedArrivalTime,
    )
  }

  return (
    <Flex
      sx={({ theme }) => ({
        margin: theme.spacing.small,
        flexDirection: 'column',
        [theme.breakpoints.up('medium')]: {
          width: '50%',
          maxWidth: '430px',
        },
      })}
    >
      <Typography as="h2" variant="speak">
        Your install bookings
      </Typography>

      <Card backgroundColor={'gallery'} sx={{ padding: theme.spacing.small }}>
        {groupedActiveAppointments.PRE_WORK && (
          <Flex sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Flex sx={{ flexDirection: 'column' }}>
              <Typography as="h3" variant="speak">
                Outside install date
              </Typography>
              <Typography as="p" strikeThrough={isApptUpdated.PRE_WORK} variant="speak">
                {isApptUpdated.PRE_WORK && <ScreenReaderOnly>Original appointment:</ScreenReaderOnly>}
                {getOriginalAppointmentDateFormatted(EVisitType.PRE_WORK)}
              </Typography>

              {isApptUpdated.PRE_WORK && (
                <Typography as="p" sx={{ marginBottom: theme.spacing.small }} variant="speak">
                  <ScreenReaderOnly>Updated appointment:</ScreenReaderOnly>
                  {getUpdatedAppointmentDateFormatted(EVisitType.PRE_WORK)}
                </Typography>
              )}
            </Flex>
            {!preWorkIsAmendable && preWorkIsComplete && (
              <Icon
                color={theme.colors.green}
                height={theme.spacing.medium}
                iconKey="TickInCircleFilled"
                width={theme.spacing.medium}
              />
            )}
          </Flex>
        )}

        {groupedActiveAppointments.PRE_WORK && groupedActiveAppointments.IN_HOME && (
          <Box sx={{ marginTop: theme.spacing.small, marginBottom: theme.spacing.small }}>
            <Separator />
          </Box>
        )}

        {/* in home info */}
        {groupedActiveAppointments.IN_HOME && (
          <Flex sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Flex sx={{ flexDirection: 'column' }}>
              <Typography as="h3" variant="speak">
                Home install date
              </Typography>
              <Typography as="p" strikeThrough={isApptUpdated.IN_HOME} variant="speak">
                {isApptUpdated.IN_HOME && <ScreenReaderOnly>Original appointment:</ScreenReaderOnly>}
                {getOriginalAppointmentDateFormatted(EVisitType.IN_HOME)}
              </Typography>
              {isApptUpdated.IN_HOME && (
                <Typography as="p" variant="speak">
                  <ScreenReaderOnly>Updated appointment:</ScreenReaderOnly>
                  {getUpdatedAppointmentDateFormatted(EVisitType.IN_HOME)}
                </Typography>
              )}
            </Flex>
            {!inHomeIsAmendable && inHomeIsComplete && (
              <Icon
                color={theme.colors.green}
                height={theme.spacing.medium}
                iconKey="TickInCircleFilled"
                width={theme.spacing.medium}
              />
            )}
          </Flex>
        )}
      </Card>
    </Flex>
  )
}

export default OrderOverview
